import { useEffect, useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { SlidersHorizontal } from "lucide-react";
import {
  AnimalCard,
  Button,
  ClockIcon,
  GlobeIcon,
  PawIcon,
  SearchIcon,
  StatusBadge,
  type Column,
} from "@fawkes/ui";

import { AdvertentieSnelActies } from "../components/AdvertentieSnelActies";
import { CameraPawIcon } from "../components/CameraPawIcon";
import { FilterInklapKnop, useFilterInklap } from "../components/FilterInklap";
import { formatDate } from "../i18n/format";
import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";
import { IndeelbareTabel } from "../components/IndeelbareTabel";
import { KennismakingReacties } from "../components/KennismakingReacties";

/** One advertisement row — mirrors api `AdvertentieRowResponse`. */
interface AdvertentieRow {
  id: string;
  dierId: number | null;
  basisNaam: string | null;
  basisSoort: string | null;
  status: string;
  basisBeschikbaarheid: string | null;
  verouderd: boolean | null;
  buitenbeentje: boolean | null;
  /**
   * Hoe vaak de advertentie bekeken is op de site waarvan het tabblad openstaat; null = niet
   * bekend, niet nul. De api kiest het cijfer bij het kanaal dat dit overzicht meestuurt.
   */
  weergaven: number | null;
  favorieten: number | null;
  /**
   * Hoeveel kenmerkvragen er bij dit dier nog onbeantwoord zijn.
   *
   * <p>Geen null-geval zoals bij de twee cijfers hierboven: dit telt onze eigen database en is dus
   * altijd bekend. Nul betekent echt nul.
   */
  kenmerkenOpen: number;
  kenmerkenTotaal: number;
  /**
   * Of er genoeg dossiertekst is om de AI de antwoorden te laten voorstellen.
   *
   * <p>Bij 1569 van de 1570 advertenties staat er iets open, dus "er is werk" zegt niets meer. Dit
   * zegt wél iets: waar het snel gaat.
   */
  kenmerkenAiKanHelpen: boolean;
  /**
   * Hoeveel mensen het kennismakingsformulier voor dit dier hebben ingevuld, en hoeveel daarvan nog
   * niemand gelezen heeft.
   *
   * <p>Het nieuwe cijfer staat er apart bij omdat dat het cijfer is waar je iets mee moet: een
   * reactie die nog niet gelezen is, is iemand die nog niet is teruggebeld.
   */
  reacties: number;
  reactiesNieuw: number;
  lintHandmatig: boolean | null;
  kanaal: string;
  /**
   * Op welke sites dit dier staat: DIERMATCH en IKZOEKBAAS, elk true of false.
   *
   * <p>Beide sleutels zitten er altijd in, ook als het dier nergens op staat — zo hoeft het overzicht
   * niet te raden of een ontbrekende sleutel "uit" betekent of "onbekend".
   */
  sites: Record<string, boolean>;
  /**
   * De dag waarop dit dier de opvang uit ging, of null zolang het er nog is.
   *
   * <p>Een dier dat weg is hoort niet op de site. Dat gaat 's nachts vanzelf, maar alléén als de
   * opvang "automatisch van de site halen" aan heeft staan; staat die uit, dan bleef zo'n dier
   * stilletjes online zonder dat er ergens iets van te zien was.
   */
  vertrokkenOp: string | null;
  gewijzigdOp: string | null;
}

/** Eén telling uit de api: waar het over gaat en hoeveel er zijn. */
interface Telling {
  sleutel: string;
  aantal: number;
}

/** The paged envelope — mirrors api `AdvertentiePageResponse`. */
interface AdvertentiePage {
  content: AdvertentieRow[];
  totalElements: number;
  page: number;
  size: number;
  statusTellingen: Telling[];
  soortTellingen: Telling[];
}

/**
 * De twee sites. Je kijkt er altijd naar één: het zijn verschillende verzamelingen dieren met hun
 * eigen vragen en teksten, dus ze door elkaar tonen zou een lijst opleveren die nergens echt bij hoort.
 */
const SITES = ["IKZOEKBAAS", "DIERMATCH"] as const;

/**
 * De sites waarop deze organisatie publiceert, volgens Instellingen → Advertentie. Publiceert ze maar
 * op één, dan hoort de andere hier niet te staan: dat is een tabblad naar een lijst die per definitie
 * leeg blijft. Zolang de instelling nog niet opgehaald is tonen we allebei — dat is wat er vóór deze
 * regel altijd gebeurde, en het verspringt niet zichtbaar omdat de lijst dan toch nog laadt.
 */
export function sitesVan(kanaal: string | undefined): readonly ("IKZOEKBAAS" | "DIERMATCH")[] {
  if (kanaal === "DIERMATCH") return ["DIERMATCH"];
  if (kanaal === "IKZOEKBAAS") return ["IKZOEKBAAS"];
  return SITES;
}

/** Eén opvang uit /api/vestigingen — de set waar deze gebruiker bij mag. */
interface Vestiging {
  id: number;
  naam: string;
}

/** Server page size — the api caps `size` at 50. */
const PAGE_SIZE = 50;

type Weergave = "kaart" | "compact" | "lijst";

const WEERGAVE_SLEUTEL = "fawkes.advertenties.weergave";

function leesWeergave(): Weergave {
  const opgeslagen = globalThis.localStorage?.getItem(WEERGAVE_SLEUTEL);
  return opgeslagen === "kaart" || opgeslagen === "compact" || opgeslagen === "lijst" ? opgeslagen : "lijst";
}

/** "beschikbaar" staat zo in het dossier; op een kaart leest een hoofdletter prettiger. */
function metHoofdletter(waarde: string | null): string {
  if (!waarde) return "—";
  return waarde.charAt(0).toUpperCase() + waarde.slice(1);
}

/**
 * Het plaatje op de soorttegel. Dezelfde set als bij Aanwezig; wat er niet bij staat krijgt "overig",
 * en dat is precies wat die tegel daar ook zegt.
 */
function soortIcoon(naam: string): string {
  const n = (naam ?? "").toLowerCase();
  if (n.includes("hond")) return "/species/hond.png";
  if (n.includes("kat")) return "/species/kat.png";
  if (n.includes("konijn")) return "/species/konijn.png";
  if (n.includes("vogel")) return "/species/vogel.png";
  if (n.includes("knaag")) return "/species/knaagdier.png";
  return "/species/overig.png";
}

// advertentie-migration (Epic 22/23 Jmix-exit): de Advertenties-overzicht. De opzet is die van
// Opvangoverzichten → Aanwezig (`AnimalOverview`): kop met tegels, een filterkaart, een resultatenbalk
// met weergave + sortering, en daaronder kaarten of een lijst. De tegels zijn hier de statussen in
// plaats van de diersoorten — dat is waar je een advertentie op zoekt.
//
// De api filtert en telt server-side (`GET /api/herplaatsing?q&status&soort&beschikbaarheid&kanaal&
// verouderd&sort&page&size` → {content,totalElements,statusTellingen,soortTellingen}); gelezen met
// `useCustom` (NIET useList — de simple-rest provider kent de envelope niet). De url is RELATIEF
// ("herplaatsing?…") — de dataProvider zet er één keer `/api` voor. Een rij opent het detailscherm.
export function AdvertentieList({ onOpenDier, onPortaalInstellingen }: {
  onOpenDier?: (dierId: number) => void;
  /** Naar de portaalinstellingen; zonder dit blijft de knop weg. */
  onPortaalInstellingen?: () => void;
} = {}) {
  // Openen gaat over het dier, niet over de advertentie: een rij zonder dier valt niets te openen.
  const open = (a: AdvertentieRow) => {
    if (a.dierId != null) onOpenDier?.(a.dierId);
  };
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  // Wat je bekijkt: alles, wat op de site staat, wat er niet op staat, of de dieren die nog een
  // advertentie moeten krijgen. Losse statussen zijn er niet meer — hij staat erop of niet.
  const [opSite, setOpSite] = useState("");
  const [soort, setSoort] = useState("");
  const [beschikbaarheid, setBeschikbaarheid] = useState("");
  const [site, setSite] = useState<string>("IKZOEKBAAS");
  const [gekozenVestigingen, setGekozenVestigingen] = useState<number[]>([]);
  const [vestigingGezet, setVestigingGezet] = useState(false);
  const [vestigingOpen, setVestigingOpen] = useState(false);
  const [vestigingZoek, setVestigingZoek] = useState("");
  const [verouderd, setVerouderd] = useState("");
  // "" = alles, "JA" = alleen waar de AI de open vragen kan voorstellen, "NEE" = alleen zonder
  // dossiertekst. Het filter dat bij deze aantallen werkt: op "heeft open vragen" valt niets weg.
  const [aiKanHelpen, setAiKanHelpen] = useState("");
  // De advertentie waarvan je de reacties leest; null = geen paneel open.
  const [reactiesVan, setReactiesVan] = useState<AdvertentieRow | null>(null);
  const [sort, setSort] = useState("GEWIJZIGD");
  const [weergave, setWeergave] = useState<Weergave>(() => leesWeergave());
  const [page, setPage] = useState(1);

  /**
   * Of je naar DierMatch kijkt. Twee cijfers kunnen alleen daarvandaan komen: het hartje waarmee een
   * bezoeker een dier bewaart, en het kennismakingsformulier. ikzoekbaas heeft geen van beide — daar
   * staat "Neem contact op" met het telefoonnummer van het asiel. Beide tabbladen lezen dezelfde
   * publicatie, dus zonder deze grens leest een reactie die op DierMatch is ingevuld als een reactie
   * via ikzoekbaas, en dat is precies wat niet kan (Maarten, 2026-10-30).
   */
  const opDierMatch = site === "DIERMATCH";

  // Sorteer je op reacties en stap je over naar ikzoekbaas, dan sorteert de lijst verder op een
  // kolom die er niet meer staat -- zichtbaar als een volgorde zonder uitleg. Terug naar de gewone.
  useEffect(() => {
    if (!opDierMatch && sort === "REACTIES") setSort("GEWIJZIGD");
  }, [opDierMatch, sort]);

  // Debounce the search box; a new search resets to page 1.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebounced(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [search]);

  // Elk ander filter zet je terug op de eerste pagina: pagina 7 van een nieuw filter bestaat meestal niet.
  useEffect(() => {
    setPage(1);
  }, [opSite, soort, beschikbaarheid, site, verouderd, aiKanHelpen, gekozenVestigingen, sort]);

  // De opvangen waar deze gebruiker bij mag; wie alles beheert krijgt ze allemaal terug.
  const { data: vestigingenData } = useCustom<Vestiging[]>({ url: "vestigingen", method: "get" });
  const vestigingen = vestigingenData?.data ?? [];
  const { data: huidigeData } = useCustom<Vestiging>({ url: "vestigingen/current", method: "get" });
  const huidige = huidigeData?.data;

  // Je begint bij je eigen opvang — dat is bijna altijd wat je wilt zien. Daarna is het jouw keuze,
  // dus dit gebeurt één keer en niet bij elke verversing.
  useEffect(() => {
    if (!vestigingGezet && huidige?.id != null) {
      setGekozenVestigingen([huidige.id]);
      setVestigingGezet(true);
    }
  }, [huidige, vestigingGezet]);

  // Buiten de opvangkiezer klikken sluit hem — anders blijft hij over de tabel hangen.
  useEffect(() => {
    if (!vestigingOpen) return undefined;
    const sluit = (e: MouseEvent) => {
      if ((e.target as HTMLElement | null)?.closest(".fawkes-fin__multi")) return;
      setVestigingOpen(false);
    };
    const toets = (e: KeyboardEvent) => {
      if (e.key === "Escape") setVestigingOpen(false);
    };
    document.addEventListener("mousedown", sluit);
    document.addEventListener("keydown", toets);
    return () => {
      document.removeEventListener("mousedown", sluit);
      document.removeEventListener("keydown", toets);
    };
  }, [vestigingOpen]);

  // De twee kleine ingrepen die je vanuit het overzicht wilt doen: in het zonnetje zetten en de
  // beschikbaarheid omzetten. Ze gaan meteen mee naar de site als de advertentie online staat.
  const { mutate: snelActie, isLoading: actieBezig } = useCustomMutation();
  // De stand op de site is één keuze met een eigen ingang; "null" betekent terug naar automatisch.
  const doeStand = (advertentieId: string, stand: string | null) => {
    snelActie(
      { url: `herplaatsing/${advertentieId}/stand`, method: "post", values: { stand },
        successNotification: false, errorNotification: false },
      { onSuccess: () => { void refetch(); } },
    );
  };

  // Een dier op één site aan- of uitzetten, vanuit het overzicht. De andere site blijft zoals hij was.
  // Welke rij bezig is onthouden we, zodat alleen die knop even uit staat en de rest bruikbaar blijft.
  const [siteBezig, setSiteBezig] = useState<string | null>(null);
  const [siteFout, setSiteFout] = useState<string | null>(null);
  const wisselSite = (rij: AdvertentieRow, doelSite: string, aan: boolean) =>
    new Promise<void>((klaar) => {
      setSiteBezig(rij.id);
      setSiteFout(null);
      snelActie(
        { url: `herplaatsing/${rij.id}/site`, method: "post", values: { site: doelSite, aan },
          successNotification: false, errorNotification: false },
        {
          onSuccess: () => { setSiteBezig(null); void refetch(); klaar(); },
          onError: (fout) => {
            setSiteBezig(null);
            // De guard geeft de redenen mee (geen foto, privacy niet nagelopen, …); die zijn het
            // antwoord op "waarom kan dit dier er niet op", dus die horen in beeld en niet in de console.
            const body = (fout as { response?: { data?: { redenen?: string[]; message?: string } } })?.response?.data;
            setSiteFout(body?.redenen?.join(" · ") ?? body?.message ?? t("advertentieListView.opSite.mislukt"));
            klaar();
          },
        },
      );
    });

  // Op welke site de organisatie publiceert (Instellingen → Advertentie). Publiceert ze op allebei, dan
  // begin je bij ikzoekbaas en schakel je zelf. Eén instelling voor alle asielen, dus de gekozen opvang
  // doet er voor deze vraag niet meer toe.
  const { data: kanaalData } = useCustom<{ kanaal: string }>({
    url: "herplaatsing/kanaal",
    method: "get",
  });
  const kanaalVanOpvang = kanaalData?.data?.kanaal;
  const zichtbareSites = useMemo(() => sitesVan(kanaalVanOpvang), [kanaalVanOpvang]);

  // Van opvang wisselen brengt je naar de site waar die opvang op staat; daarna schakel je zelf.
  useEffect(() => {
    if (kanaalVanOpvang === "DIERMATCH" || kanaalVanOpvang === "IKZOEKBAAS") {
      setSite(kanaalVanOpvang);
    } else if (kanaalVanOpvang === "BEIDE") {
      setSite("IKZOEKBAAS");
    }
  }, [kanaalVanOpvang]);

  const url = useMemo(() => {
    const params = new URLSearchParams();
    if (debounced) params.set("q", debounced);
    if (opSite === "JA") params.set("opSite", "true");
    if (opSite === "NEE") params.set("opSite", "false");
    if (opSite === "TEDOEN") params.set("modus", "TEDOEN");
    if (soort) params.set("soort", soort);
    if (beschikbaarheid) params.set("beschikbaarheid", beschikbaarheid);
    params.set("kanaal", site);
    if (verouderd) params.set("verouderd", verouderd);
    if (aiKanHelpen) params.set("aiKanHelpen", aiKanHelpen === "JA" ? "true" : "false");
    for (const v of gekozenVestigingen) params.append("vestigingId", String(v));
    params.set("sort", sort);
    params.set("page", String(page - 1)); // UI is 1-based; the api page is 0-based.
    params.set("size", String(PAGE_SIZE));
    return `herplaatsing?${params.toString()}`;
  }, [debounced, opSite, soort, beschikbaarheid, site, verouderd, aiKanHelpen, gekozenVestigingen, sort, page]);

  // Elke minuut opnieuw ophalen, zonder dat iemand op Verversen drukt: een reactie die binnenkomt
  // hoort vanzelf in de kolom te verschijnen. De knop blijft staan voor wie niet wil wachten
  // (Maarten, 2026-10-29).
  const { data, isLoading, isError, refetch } = useCustom<AdvertentiePage>({
    url,
    method: "get",
    queryOptions: { refetchInterval: 60_000 },
  });

  const rows = data?.data?.content ?? [];
  const total = data?.data?.totalElements ?? 0;
  const soortTellingen = data?.data?.soortTellingen ?? [];
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  // De tegels tellen los van het soortfilter, dus "alles" is hun som — niet het gefilterde totaal.
  const totaalAlleSoorten = soortTellingen.reduce((som, s) => som + s.aantal, 0);

  // De foto's van de dieren op de kaarten: authenticated blob → object-url, net als bij Aanwezig.
  const fotoUrls = useAnimalPhotoUrls(rows.map((a) => a.dierId).filter((id): id is number => id != null));

  // De opvang en de site tellen hier niet als filter: dat is waar je werkt en waar je naar kijkt,
  // niet iets wat je even aanzet.
  const filtersActief = Boolean(
    search || opSite || soort || beschikbaarheid || verouderd || aiKanHelpen || sort !== "GEWIJZIGD",
  );

  // Op een telefoon staat het filterblok dicht; alleen het zoekveld blijft staan. Zie
  // FilterInklap. Op een breed scherm is `toon` altijd waar en `mobiel` nooit. Het zoekveld
  // telt hier niet mee op de knop: dat staat er zichtbaar boven.
  const { mobiel, open: filtersOpen, toon, wissel } = useFilterInklap();
  const filterActief =
    (opSite ? 1 : 0) + (soort ? 1 : 0) + (beschikbaarheid ? 1 : 0) + (verouderd ? 1 : 0)
    + (aiKanHelpen ? 1 : 0);

  const wisFilters = () => {
    setSearch("");
    setOpSite("");
    setSoort("");
    setBeschikbaarheid("");
    setVerouderd("");
    setAiKanHelpen("");
    setSort("GEWIJZIGD");
  };

  const zetWeergave = (w: Weergave) => {
    setWeergave(w);
    globalThis.localStorage?.setItem(WEERGAVE_SLEUTEL, w);
  };

  const alleKolommen: Column<AdvertentieRow>[] = [
    {
      // Een dier herken je aan zijn kop, niet aan zijn naam — zeker bij "Kitten 25-96".
      header: t("advertentieListView.column.foto"),
      cell: (a) => {
        const url = a.dierId == null ? null : fotoUrls[String(a.dierId)] ?? null;
        return url ? (
          <span className="da-overview__thumb">
            <img src={url} alt={a.basisNaam ?? ""} />
          </span>
        ) : (
          <span className="da-overview__thumb da-overview__thumb--empty" aria-hidden="true">
            <CameraPawIcon size={18} strokeWidth={1.5} />
          </span>
        );
      },
    },
    {
      header: t("advertentieListView.column.dier"),
      cell: (a) => (
        <span className="fawkes-adv-overzicht__naam">
          {a.basisNaam ?? "—"}
          {a.buitenbeentje === true && (
            <span className="fawkes-adv-overzicht__zon" title={t("advertentieListView.actie.zonnetjeAan")}>
              ☀
            </span>
          )}
        </span>
      ),
    },
    { header: t("advertentieListView.column.soort"), cell: (a) => a.basisSoort ?? "—" },
    {
      header: t("advertentieListView.column.opSite"),
      // Een knop en geen label: dit is de plek waar iemand een dier op een site wil zetten, en dan
      // hoort hij daar te kunnen klikken in plaats van het dier te openen. De schakelaar gaat over
      // de site waarvan het tabblad openstaat — daarom staat die naam er ook bij.
      cell: (a) => {
        if (a.status === "GEEN") {
          return <StatusBadge tone="neutral">{t("advertentieListView.status.geen")}</StatusBadge>;
        }
        const doelSite = site === "DIERMATCH" ? "DIERMATCH" : "IKZOEKBAAS";
        const staatErop = a.sites?.[doelSite] ?? false;
        const bezig = siteBezig === a.id;
        // Weg uit de opvang en tóch nog online: dat hoort niet, en tot nu toe zag niemand het. De
        // waarschuwing staat bij de schakelaar en niet in een aparte kolom, want dit gaat precies
        // over die schakelaar (Maarten, 02-11-2026).
        if (staatErop && a.vertrokkenOp) {
          return (
            <div className="adv-site-cel">
              <button
                type="button"
                className="adv-site-knop adv-site-knop--aan"
                data-testid="site-schakelaar"
                disabled={bezig}
                aria-pressed
                title={t("advertentieListView.opSite.haalEraf")}
                onClick={(e) => {
                  e.stopPropagation();
                  void wisselSite(a, doelSite, false);
                }}
              >
                <span className="adv-site-knop__stip" aria-hidden="true" />
                {bezig ? t("advertentieListView.opSite.bezig") : t("advertentieListView.opSite.ja")}
              </button>
              <span className="adv-site-weg" data-testid="site-dier-weg">
                {t("advertentieListView.opSite.dierWeg", { args: [formatDate(a.vertrokkenOp, locale)] })}
              </span>
            </div>
          );
        }
        return (
          <button
            type="button"
            className={`adv-site-knop${staatErop ? " adv-site-knop--aan" : ""}`}
            data-testid="site-schakelaar"
            disabled={bezig}
            aria-pressed={staatErop}
            title={t(staatErop
              ? "advertentieListView.opSite.haalEraf"
              : "advertentieListView.opSite.zetErop")}
            onClick={(e) => {
              e.stopPropagation();
              void wisselSite(a, doelSite, !staatErop);
            }}
          >
            <span className="adv-site-knop__stip" aria-hidden="true" />
            {bezig
              ? t("advertentieListView.opSite.bezig")
              : t(staatErop ? "advertentieListView.opSite.ja" : "advertentieListView.opSite.nee")}
          </button>
        );
      },
    },
    {
      header: t("advertentieListView.column.beschikbaarheid"),
      cell: (a) => a.basisBeschikbaarheid ?? "—",
    },
    {
      header: t("advertentieListView.column.bekeken"),
      // Het cijfer van de site waarvan het tabblad openstaat; de api kiest het bij het kanaal dat
      // het overzicht meestuurt. Een streepje bij null en niet een 0: "niet gepubliceerd" en "nul
      // mensen keken" zijn twee verschillende dingen, en dat verschil moet een asiel kunnen zien.
      cell: (a) => (
        <span title={t("advertentieListView.bekeken.uitleg")}>
          {a.weergaven == null ? "—" : a.weergaven.toLocaleString(locale)}
        </span>
      ),
    },
    {
      key: "bewaard",
      header: t("advertentieListView.column.bewaard"),
      // Naast de weergaven, want samen zeggen ze iets wat geen van beide alleen zegt: vaak bekeken
      // en door niemand bewaard is gezien maar niet onthouden. Streepje bij null, om dezelfde
      // reden als hierboven.
      cell: (a) => (
        <span title={t("advertentieListView.bewaard.uitleg")}>
          {a.favorieten == null ? "—" : a.favorieten.toLocaleString(locale)}
        </span>
      ),
    },
    {
      key: "reacties",
      header: t("advertentieListView.column.reacties"),
      // Naast Bekeken en Bewaard, want het is dezelfde soort cijfer — wat de site teruggeeft — maar
      // het enige waar een mens aan te pas komt: hier staat iemand te wachten op een telefoontje.
      // Een streepje bij nul en geen "0": nul reacties is geen werk, en een kolom vol nullen leest
      // als een rij lege vakjes.
      cell: (a) =>
        a.reacties > 0 ? (
          <button
            type="button"
            className={a.reactiesNieuw > 0 ? "fawkes-adv__reacties is-nieuw" : "fawkes-adv__reacties"}
            title={t("advertentieListView.reacties.uitleg")}
            onClick={(e) => {
              e.stopPropagation();
              setReactiesVan(a);
            }}
          >
            {(a.reactiesNieuw > 0
              ? t("advertentieListView.reacties.nieuw")
              : t("advertentieListView.reacties.aantal")
            )
              .replace("%n%", String(a.reacties))
              .replace("%nieuw%", String(a.reactiesNieuw))}
          </button>
        ) : (
          /* Ook zonder reacties een knop: daarachter staan de antwoorden op volgorde van binnenkomst
             en de extra vragen die je bij dit dier stelt. Een streepje dat niets doet verbergt dat
             (Maarten, 2026-10-29). Stil vormgegeven, want nul reacties is geen werk. */
          <button
            type="button"
            className="fawkes-adv__reacties is-leeg"
            title={t("advertentieListView.reacties.leeg")}
            onClick={(e) => {
              e.stopPropagation();
              setReactiesVan(a);
            }}
          >
            —
          </button>
        ),
    },
    {
      header: t("advertentieListView.column.vragen"),
      // Stond eerst als werklijst bóven het overzicht. Die vertelde hoe groot het werk was, maar niet
      // bij wélk dier je zat te kijken; hier valt het op bij de rij waar het over gaat (Maarten,
      // 2026-10-28). Rood en niet oranje: een advertentie met open vragen staat wél op de site maar
      // doet in de matchwizard nauwelijks mee, want die leest precies deze antwoorden.
      //
      // Alleen een knop als er iets te doen is. Een "compleet"-vink op elke andere rij zou de kolom
      // vullen met ruis en juist het rode wegdrukken.
      cell: (a) =>
        a.kenmerkenOpen > 0 ? (
          <button
            type="button"
            // Twee kleuren, want bij deze aantallen is "er staat iets open" geen onderscheid meer:
            // dat geldt voor bijna alles. Waar het dossier genoeg tekst heeft stelt de AI de
            // antwoorden voor en hoef je ze na te kijken; daar begint het werk.
            className={
              a.kenmerkenAiKanHelpen ? "fawkes-adv__vragen is-ai" : "fawkes-adv__vragen"
            }
            title={t(
              a.kenmerkenAiKanHelpen
                ? "advertentieListView.vragen.uitlegAi"
                : "advertentieListView.vragen.uitleg",
            )}
            onClick={(e) => {
              e.stopPropagation();
              if (a.dierId != null) onOpenDier?.(a.dierId);
            }}
          >
            {t(
              a.kenmerkenAiKanHelpen
                ? "advertentieListView.vragen.openAi"
                : "advertentieListView.vragen.open",
            ).replace("%n%", String(a.kenmerkenOpen))}
          </button>
        ) : (
          <button
            type="button"
            className="fawkes-adv__vragen is-klaar"
            title={t("advertentieListView.vragen.klaarUitleg")}
            onClick={(e) => {
              e.stopPropagation();
              if (a.dierId != null) onOpenDier?.(a.dierId);
            }}
          >
            {t("advertentieListView.vragen.klaar")}
          </button>
        ),
    },
    {
      header: t("advertentieListView.column.verouderd"),
      cell: (a) =>
        a.verouderd ? (
          <StatusBadge tone="warning">{t("advertentieListView.verouderd.yes")}</StatusBadge>
        ) : (
          t("advertentieListView.verouderd.no")
        ),
    },
    {
      header: t("advertentieListView.column.gewijzigd"),
      cell: (a) => formatDate(a.gewijzigdOp, locale),
    },
    {
      header: "",
      // Geen ⋯-menu meer: twee knoppen die hun eigen stand tonen. Wie in een lijst van honderd
      // advertenties tien dieren op gereserveerd zet, doet dat nu met tien klikken in plaats van
      // twintig. Openen zit al op de rij zelf (onRowClick), dus daar hoefde geen regel voor terug.
      cell: (a) => (
        <AdvertentieSnelActies
          compact
          buitenbeentje={a.buitenbeentje}
          beschikbaarheid={a.basisBeschikbaarheid}
          status={a.status}
          busy={actieBezig}
          onStand={(stand) => doeStand(a.id, stand)}
        />
      ),
    },
  ];

  // Weglaten en niet op nul zetten: een kolom die op dit tabblad nooit iets kan tonen is een kolom
  // die belooft dat er iets te meten valt.
  const columns = alleKolommen.filter(
    (k) => opDierMatch || (k.key !== "bewaard" && k.key !== "reacties"),
  );

  // Op de kaart alleen wat iets zegt: het kanaal en wanneer er voor het laatst aan gewerkt is. De
  // beschikbaarheid komt er alleen bij als die afwijkt van gewoon "beschikbaar" — dat is de regel die
  // ikzoekbaas zelf ook hanteert, en anders staat op elke kaart dezelfde regel.
  const metaRegels = (a: AdvertentieRow) => {
    const regels = [
      {
        key: "kanaal",
        icon: <GlobeIcon width={14} height={14} />,
        label: t("advertentieListView.filter.kanaal"),
        value: t(`advertentieKanaal.${a.kanaal}`),
      },
      {
        key: "gewijzigd",
        icon: <ClockIcon width={14} height={14} />,
        label: t("advertentieListView.card.gewijzigd"),
        value: formatDate(a.gewijzigdOp, locale) || "—",
      },
    ];
    const afwijkend = (a.basisBeschikbaarheid ?? "").trim().toLowerCase();
    if (afwijkend && afwijkend !== "beschikbaar") {
      regels.unshift({
        key: "beschikbaarheid",
        icon: <PawIcon width={14} height={14} />,
        label: t("advertentieListView.card.beschikbaarheid"),
        value: metHoofdletter(a.basisBeschikbaarheid),
      });
    }
    return regels;
  };

  const zichtbareVestigingen = vestigingen.filter((v) =>
    v.naam.toLowerCase().includes(vestigingZoek.trim().toLowerCase()));

  // Niets gekozen betekent al je opvangen; één gekozen leest prettiger als zijn naam dan als "1 gekozen".
  const vestigingKnopTekst = gekozenVestigingen.length === 0
    ? t("advertentieListView.filter.alleVestigingen")
    : gekozenVestigingen.length === 1
      ? vestigingen.find((v) => v.id === gekozenVestigingen[0])?.naam
        ?? t("advertentieListView.filter.alleVestigingen")
      : t("advertentieListView.filter.vestigingGekozen", { args: [gekozenVestigingen.length] });

  const knopKlasse = (actief: boolean) =>
    `da-overview__viewbtn${actief ? " da-overview__viewbtn--active" : ""}`;

  return (
    <div className="da-overview fawkes-adv-overzicht">
      {/* Kop: titel + wat dit scherm is, met de statussen als tegels — aanklikken filtert. */}
      <div className="da-overview__header">
        <div className="da-overview__intro">
          <span className="da-overview__icon" aria-hidden="true"><GlobeIcon width={30} height={30} /></span>
          <div>
            <h1 className="da-overview__title">{t("advertentieListView.title")}</h1>
            <p className="da-overview__subtitle">{t("advertentieListView.subtitle")}</p>
            {/* Welke site je bekijkt — je kijkt er altijd naar één. Een advertentie die op allebei
                staat komt in allebei de lijsten voor, dus schakelen verbergt niets wat er wél op staat. */}
            {zichtbareSites.length > 1 && (
            <div className="fawkes-adv-keuze" role="group" aria-label={t("advertentieListView.site")}>
              {zichtbareSites.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={"fawkes-adv-keuze__optie" + (site === s ? " fawkes-adv-keuze__optie--actief" : "")}
                  aria-pressed={site === s}
                  onClick={() => setSite(s)}
                >
                  {t(`advertentieKanaal.${s}`)}
                </button>
              ))}
            </div>
            )}
            {/* Wat er op de site gebeurt met je advertenties stel je hier in, naast het overzicht
                waar je ze beheert — en niet ergens onder Instellingen, waar niemand het zoekt. */}
            {onPortaalInstellingen && (
              <button type="button" className="fawkes-adv-portaalknop" onClick={onPortaalInstellingen}>
                <SlidersHorizontal size={16} aria-hidden="true" />
                {t("menu.portaalInstellingen")}
              </button>
            )}
          </div>
        </div>
        {/* De tegels zijn de diersoorten, net als bij Aanwezig: zo zoek je een dier. De status is een
            filter in de balk eronder — sinds de standen knoppen per rij zijn, is dat geen ingang meer. */}
        <div className="da-overview__stats">
          {soortTellingen.map((sc) => (
            <button
              key={sc.sleutel}
              type="button"
              className={`da-overview__stat${soort === sc.sleutel ? " da-overview__stat--active" : ""}`}
              onClick={() => setSoort(soort === sc.sleutel ? "" : sc.sleutel)}
            >
              <span className="da-overview__stat-icon">
                <img src={soortIcoon(sc.sleutel)} alt="" />
              </span>
              <span className="da-overview__stat-body">
                <span className="da-overview__stat-value">{sc.aantal}</span>
                <span className="da-overview__stat-label">{sc.sleutel}</span>
              </span>
            </button>
          ))}
          <button
            type="button"
            className={`da-overview__stat${soort === "" ? " da-overview__stat--active" : ""}`}
            onClick={() => setSoort("")}
          >
            <span className="da-overview__stat-icon da-overview__stat-icon--total" aria-hidden="true">
              <PawIcon width={22} height={22} />
            </span>
            <span className="da-overview__stat-body">
              <span className="da-overview__stat-value">{totaalAlleSoorten}</span>
              <span className="da-overview__stat-label">{t("advertentieListView.stat.total")}</span>
            </span>
          </button>
        </div>
      </div>

      {/* Filterkaart: zoeken plus de filters op één rij, met "Wis filters" aan het eind. */}
      <div className="da-overview__filters">
        <div className="da-overview__filter-grid">
          <div className="fawkes-products-search da-overview__search">
            <span className="fawkes-products-search__icon" aria-hidden="true">🔍</span>
            <input
              type="search"
              autoComplete="off"
              className="fawkes-products-search__input"
              placeholder={t("advertentieListView.search.placeholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={t("advertentieListView.search.placeholder")}
            />
          </div>

          {mobiel && (
            <FilterInklapKnop
              open={filtersOpen}
              actief={filterActief}
              label={t("filter.inklap")}
              onClick={wissel}
            />
          )}

          {toon && (
          <>
          <label className="da-overview__field">
            <span className="da-overview__field-label">{t("advertentieListView.filter.opSite")}</span>
            <select className="da-overview__filter" value={opSite} onChange={(e) => setOpSite(e.target.value)}>
              <option value="">{t("advertentieListView.filter.all")}</option>
              <option value="JA">{t("advertentieListView.opSite.ja")}</option>
              <option value="NEE">{t("advertentieListView.opSite.nee")}</option>
              {/* De dieren die er nog niet op staan en er wel op horen: aanwezig, geen advertentie,
                  en zwervers en TNRC pas na twee weken — die kunnen nog terug naar hun eigen baas. */}
              <option value="TEDOEN">{t("advertentieListView.opSite.teDoen")}</option>
            </select>
          </label>

          <label className="da-overview__field">
            <span className="da-overview__field-label">
              {t("advertentieListView.column.beschikbaarheid")}
            </span>
            <select
              className="da-overview__filter"
              value={beschikbaarheid}
              onChange={(e) => setBeschikbaarheid(e.target.value)}
            >
              <option value="">{t("advertentieListView.filter.all")}</option>
              <option value="beschikbaar">{t("advertentieListView.beschikbaarheid.beschikbaar")}</option>
              <option value="gereserveerd">{t("advertentieListView.beschikbaarheid.gereserveerd")}</option>
              <option value="nog niet beschikbaar">
                {t("advertentieListView.beschikbaarheid.nogNiet")}
              </option>
            </select>
          </label>

          {/* Meer dan één opvang tegelijk is normaal werk (een regio, of "alles behalve die ene"), dus
              vinkjes met een zoekveld in plaats van één keuze. Bij één opvang valt er niets te kiezen. */}
          {vestigingen.length > 1 ? (
            <div className="da-overview__field fawkes-fin__multi">
              <span className="da-overview__field-label">{t("advertentieListView.filter.vestiging")}</span>
              <button
                type="button"
                className="fawkes-fin__multi-btn"
                aria-haspopup="true"
                aria-expanded={vestigingOpen}
                onClick={() => setVestigingOpen((open) => !open)}
              >
                <span>{vestigingKnopTekst}</span>
                <span aria-hidden="true">▾</span>
              </button>
              {vestigingOpen && (
                <div
                  className="fawkes-fin__multi-menu"
                  role="group"
                  aria-label={t("advertentieListView.filter.vestiging")}
                >
                  <label className="fawkes-fin__multi-zoek">
                    <SearchIcon width={14} height={14} aria-hidden="true" />
                    <input
                      value={vestigingZoek}
                      autoFocus
                      placeholder={t("advertentieListView.filter.vestigingZoek")}
                      aria-label={t("advertentieListView.filter.vestigingZoek")}
                      onChange={(e) => setVestigingZoek(e.target.value)}
                    />
                  </label>
                  {/* "Alles aan" werkt op wat je ziet: typ "asiel" en je zet die groep in één klik aan. */}
                  <button
                    type="button"
                    className="fawkes-fin__multi-all"
                    onClick={() =>
                      setGekozenVestigingen((huidig) =>
                        zichtbareVestigingen.every((v) => huidig.includes(v.id))
                          ? huidig.filter((id) => !zichtbareVestigingen.some((v) => v.id === id))
                          : [...new Set([...huidig, ...zichtbareVestigingen.map((v) => v.id)])])
                    }
                  >
                    {zichtbareVestigingen.length > 0
                      && zichtbareVestigingen.every((v) => gekozenVestigingen.includes(v.id))
                      ? t("advertentieListView.filter.allesUit")
                      : t("advertentieListView.filter.allesAan")}
                  </button>
                  <div className="fawkes-fin__multi-list">
                    {zichtbareVestigingen.length === 0 && (
                      <p className="fawkes-fin__multi-leeg">{t("advertentieListView.filter.vestigingGeen")}</p>
                    )}
                    {zichtbareVestigingen.map((v) => (
                      <label key={v.id}>
                        <input
                          type="checkbox"
                          checked={gekozenVestigingen.includes(v.id)}
                          onChange={() =>
                            setGekozenVestigingen((huidig) =>
                              huidig.includes(v.id)
                                ? huidig.filter((id) => id !== v.id)
                                : [...huidig, v.id])
                          }
                        />
                        <span>{v.naam}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <label className="da-overview__field">
            <span className="da-overview__field-label">{t("advertentieListView.column.verouderd")}</span>
            <select
              className="da-overview__filter"
              value={verouderd}
              onChange={(e) => setVerouderd(e.target.value)}
            >
              <option value="">{t("advertentieListView.filter.all")}</option>
              <option value="true">{t("advertentieListView.verouderd.only")}</option>
              <option value="false">{t("advertentieListView.verouderd.hide")}</option>
            </select>
          </label>

          {/* Bijna elke advertentie heeft open vragen, dus filteren op "er staat iets open" levert
              niets op. Dit filter wel: het zet de dieren voorop waar de AI de antwoorden kan
              voorstellen, en dat is waar je in één middag het meeste wegwerkt. */}
          <label className="da-overview__field">
            <span className="da-overview__field-label">{t("advertentieListView.filter.ai")}</span>
            <select
              className="da-overview__filter"
              value={aiKanHelpen}
              onChange={(e) => setAiKanHelpen(e.target.value)}
            >
              <option value="">{t("advertentieListView.filter.ai.alle")}</option>
              <option value="JA">{t("advertentieListView.filter.ai.ja")}</option>
              <option value="NEE">{t("advertentieListView.filter.ai.nee")}</option>
            </select>
          </label>

          <div className="da-overview__field da-overview__field--action">
            <span className="da-overview__field-label" aria-hidden="true">&nbsp;</span>
            <button
              type="button"
              className="da-overview__wis"
              onClick={wisFilters}
              disabled={!filtersActief}
            >
              <span className="da-overview__wis-icon" aria-hidden="true">↻</span>
              {t("advertentieListView.filter.clear")}
            </button>
          </div>
          </>
          )}
        </div>
      </div>

      {/* Waarom een dier niet op een site gezet kon worden. De guard noemt de redenen — geen foto,
          privacy niet nagelopen — en dat is precies het antwoord op de vraag die de gebruiker stelt. */}
      {siteFout && (
        <div className="adv-site-fout" role="alert">
          {siteFout}
          <button type="button" onClick={() => setSiteFout(null)} aria-label={t("common.close")}>×</button>
        </div>
      )}

      {/* Resultatenbalk: hoeveel er gevonden zijn, de weergave en de sortering. */}
      <div className="da-overview__resultsbar">
        <span className="da-overview__results-count">
          <PawIcon width={16} height={16} /> {total}{" "}
          {t(total === 1 ? "advertentieListView.results.foundOne" : "advertentieListView.results.found")}
        </span>
        <div className="da-overview__resultsbar-right">
          <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
            {t("advertentieListView.refresh")}
          </Button>
          <div className="da-overview__viewtoggle" role="group" aria-label={t("advertentieListView.viewMode")}>
            <button
              type="button"
              className={knopKlasse(weergave === "kaart")}
              onClick={() => zetWeergave("kaart")}
              title={t("advertentieListView.cardMode")}
              aria-label={t("advertentieListView.cardMode")}
              aria-pressed={weergave === "kaart"}
            >
              <span aria-hidden="true">▦</span>
            </button>
            <button
              type="button"
              className={knopKlasse(weergave === "compact")}
              onClick={() => zetWeergave("compact")}
              title={t("advertentieListView.compactMode")}
              aria-label={t("advertentieListView.compactMode")}
              aria-pressed={weergave === "compact"}
            >
              <span aria-hidden="true">▥</span>
            </button>
            <button
              type="button"
              className={knopKlasse(weergave === "lijst")}
              onClick={() => zetWeergave("lijst")}
              title={t("advertentieListView.listMode")}
              aria-label={t("advertentieListView.listMode")}
              aria-pressed={weergave === "lijst"}
            >
              <span aria-hidden="true">☰</span>
            </button>
          </div>
          <label className="da-overview__sort">
            <span className="da-overview__sort-label">{t("advertentieListView.sortLabel")}</span>
            <select className="da-overview__filter" value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="GEWIJZIGD">{t("advertentieListView.sort.gewijzigd")}</option>
              <option value="NAAM">{t("advertentieListView.sort.naam")}</option>
              {/* Reacties bovenaan: eerst wie nog niet is teruggebeld, dan wie de meeste
                  belangstelling heeft. De api sorteert hiervoor buiten de database om, want die
                  tellingen staan ergens anders. Alleen op DierMatch, want alleen daar kun je
                  reageren -- sorteren op een kolom die er niet staat is een lege belofte. */}
              {opDierMatch && (
                <option value="REACTIES">{t("advertentieListView.sort.reacties")}</option>
              )}
            </select>
          </label>
        </div>
      </div>

      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError ? (
        <p className="fawkes-error">{t("advertentieListView.loadFailed")}</p>
      ) : rows.length === 0 ? (
        <p className="fawkes-muted">{t("advertentieListView.empty")}</p>
      ) : weergave === "lijst" ? (
        <div className="fawkes-adv-overzicht__tabel">
        <IndeelbareTabel tabelKey="advertentie-list"
          columns={columns}
          rows={rows}
          getRowKey={(a) => a.id}
          empty={t("advertentieListView.empty")}
          onRowClick={open}
        />
        </div>
      ) : (
        <div className={`da-overview__grid${weergave === "compact" ? " da-overview__grid--compact" : ""}`}>
          {rows.map((a) => (
            <AnimalCard
              key={a.id}
              compact={weergave === "compact"}
              name={a.basisNaam ?? "—"}
              subtitle={a.basisSoort ?? undefined}
              photoUrl={a.dierId == null ? null : fotoUrls[String(a.dierId)] ?? null}
              noPhotoLabel={t("advertentieListView.card.noPhoto")}
              statusBadge={t(`advertentieStatus.${a.status}`)}
              meta={metaRegels(a)}
              onOpen={() => open(a)}
              actions={
                <AdvertentieSnelActies
                  compact
                  buitenbeentje={a.buitenbeentje}
                  beschikbaarheid={a.basisBeschikbaarheid}
                  status={a.status}
                  busy={actieBezig}
                          onStand={(stand) => doeStand(a.id, stand)}
                />
              }
            />
          ))}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="da-overview__pager">
          <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            ‹
          </Button>
          <span className="da-overview__pager-label">
            {t("advertentieListView.pagination.range", { args: [from, to, total] })}
          </span>
          <Button
            variant="secondary"
            disabled={page >= pageCount}
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          >
            ›
          </Button>
        </div>
      )}

      {reactiesVan != null && (
        <KennismakingReacties
          advertentieId={reactiesVan.id}
          dierNaam={reactiesVan.basisNaam}
          onClose={() => setReactiesVan(null)}
          // Lezen verandert het cijfer in de kolom, dus de lijst moet het opnieuw ophalen. Niet bij
          // het sluiten: dan zie je het getal pas veranderen nadat je het paneel al weg hebt.
          onGelezen={() => void refetch()}
        />
      )}
    </div>
  );
}
